import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { DATA_STALE_TIME } from "~/config/constants";
import {
  ApplicationApi,
  FeeWaiverCodeApi,
  GetApi,
  IApplicationDocumentDelete,
} from "~/api";
import { useNotification } from "~/components/core/NotificationProvider";

export function useApplication(applicationId?: string) {
  return useQuery({
    queryKey: ["applications", applicationId],
    queryFn: () => GetApi(ApplicationApi).getApplication(applicationId ?? ""),
    enabled: Boolean(applicationId),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: DATA_STALE_TIME,
  });
}

export function useApplicationDocumentChecklistItems(applicationId?: string) {
  return useQuery({
    queryKey: ["applications", applicationId, "documents"],
    queryFn: () => GetApi(ApplicationApi).getApplicationDocuments(applicationId ?? ""),
    enabled: Boolean(applicationId),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: DATA_STALE_TIME,
  });
}

export function useDeleteApplicationDocument(applicationId?: string) {
  const client = useQueryClient();
  const { showNotification, showErrorNotification } = useNotification();

  return useMutation({
    mutationKey: ["applications", applicationId, "documents"],
    mutationFn: (deletePayload: IApplicationDocumentDelete) =>
      GetApi(ApplicationApi).deleteApplicationDocument(
        applicationId ?? "",
        deletePayload,
      ),
    onSuccess: (_, { filename }) => {
      const message = filename
        ? `Document "${filename}" deleted successfully.`
        : "Document deleted successfully.";
      showNotification({ type: "success", message });
    },
    onError: (error) => {
      showErrorNotification(error, "Document deletion failed");
    },
    onSettled: () => {
      client.invalidateQueries({
        queryKey: ["applications", applicationId, "documents"],
      });
    },
  });
}

export function useVerifyFeeWaiverCode(code?: string) {
  return useQuery({
    queryKey: ["verifyFeeWaiverCode", code],
    queryFn: () =>
      code
        ? GetApi(FeeWaiverCodeApi).getFeeWaiverCode(code)
        : Promise.reject(new Error("code is required")),
    enabled: false,
    retry: false,
  });
}

export function useUpdateFeeWaiverCode(applicationId?: string) {
  return useMutation({
    mutationKey: ["updateFeeWaiverCode", applicationId],
    mutationFn: (code: string) =>
      GetApi(ApplicationApi).updateFeeWaiverCode(applicationId ?? "", {
        feeWaiverCode: code,
      }),
  });
}
